import { AxiosError } from "axios"
import DatePicker from "react-datepicker"
import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { selectCommunityInfo, selectCommunityLoading } from "@/entities/community"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import PencilSvg from "@/shared/assets/images/Pencil.svg"
import TrashSvg from "@/shared/assets/images/Trash.svg"
import {
    createPromotion,
    deletePromotion,
    IPromotion,
    IListPromotionsParams,
    IUpdatePromotionPayload,
    listPromotions,
    updatePromotion,
} from "@/shared/api/promotions"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"
import notify from "@/shared/lib/notify"
import { useAppSelector } from "@/shared/lib/redux"
import { ErrorType } from "@/shared/types"
import { ActionButton } from "@/shared/ui/action-button"
import { Loader } from "@/shared/ui/loader"
import { Modal } from "@/shared/ui/modal"
import { NoTableData } from "@/shared/ui/no-table-data"
import { PageHeader } from "@/widgets/page-header"

interface FiltersState {
    begin_at: Date | null
    end_at: Date | null
}

interface PromotionFormState {
    title: string
    description: string
    beginAt: string
    endAt: string
    imageFile: File | null
    imagePreview: string
}

interface DateTimeFilterFieldProps {
    label: string
    value: Date | null
    onChange: (value: Date | null) => void
}

const defaultFilters: FiltersState = {
    begin_at: null,
    end_at: null,
}

const emptyFormState: PromotionFormState = {
    title: "",
    description: "",
    beginAt: "",
    endAt: "",
    imageFile: null,
    imagePreview: "",
}

const acceptedExtensions = [".png", ".jpg", ".jpeg", ".webp"]
const acceptedMimeTypes = ["image/png", "image/jpeg", "image/webp"]
const maxImageSize = 8 * 1024 * 1024

const isBlobUrl = (url: string): boolean => url.startsWith("blob:")

const toInputDateTime = (isoDate: string | null): string => {
    if (!isoDate) {
        return ""
    }

    const date = new Date(isoDate)
    if (Number.isNaN(date.getTime())) {
        return ""
    }

    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 16)
}

const toIsoDateTime = (value: string): string | undefined => {
    if (!value) {
        return undefined
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        return undefined
    }

    return parsed.toISOString()
}

const toPickerDate = (value: string): Date | null => {
    if (!value) {
        return null
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        return null
    }

    return parsed
}

const fromPickerDate = (value: Date | null): string => {
    if (!value) {
        return ""
    }

    const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 16)
}

const CalendarContainer = ({ children }: { children: ReactNode }) => createPortal(children, document.body)

const normalizeString = (value: string): string => value.trim()

const getApiErrorMessage = (error: unknown): string => {
    const knownError = error as AxiosError<ErrorType>
    const errorId = knownError.response?.data?.error_id
    const serverMessage = knownError.response?.data?.message

    if (errorId) {
        return getErrorMessage(errorId)
    }

    if (serverMessage) {
        return serverMessage
    }

    return "Произошла непредвиденная ошибка"
}

const DateTimeFilterField = ({ label, value, onChange }: DateTimeFilterFieldProps) => {
    const handleDateChange = (date: Date | null | [Date | null, Date | null]) => {
        if (Array.isArray(date)) {
            onChange(date[0] ?? null)
            return
        }

        onChange(date)
    }

    return (
        <div className="col-span-12 sm:col-span-6 xl:col-span-3">
            <label className="text-sm text-[#828282] uppercase">{label}</label>
            <DatePicker
                selected={value}
                onChange={handleDateChange}
                wrapperClassName="react-datepicker-filterlike"
                dateFormat={"dd.MM.yyyy"}
                placeholderText="Выберите дату"
                showIcon
                enableTabLoop={false}
                popperProps={{
                    placement: "bottom",
                }}
                icon={<CalendarIcon width={24} height={24} />}
            />
        </div>
    )
}

export const PromotionsPage = () => {
    const community = useAppSelector(selectCommunityInfo)
    const communityLoading = useAppSelector(selectCommunityLoading)

    const [promotions, setPromotions] = useState<IPromotion[]>([])
    const [isListLoading, setIsListLoading] = useState(false)

    const [draftFilters, setDraftFilters] = useState<FiltersState>(defaultFilters)
    const [appliedFilters, setAppliedFilters] = useState<FiltersState>(defaultFilters)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentPromotion, setCurrentPromotion] = useState<IPromotion | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [promotionToDelete, setPromotionToDelete] = useState<IPromotion | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [formState, setFormState] = useState<PromotionFormState>(emptyFormState)

    const isEditing = Boolean(currentPromotion)
    const isPromotionsEnabled = Boolean(community?.is_promotions_enabled)
    const canShowListLoader = communityLoading || isListLoading

    const queryParams: IListPromotionsParams = useMemo(() => {
        return {
            begin_at: appliedFilters.begin_at?.toISOString(),
            end_at: appliedFilters.end_at?.toISOString(),
        }
    }, [appliedFilters])

    const fetchPromotions = async (params: IListPromotionsParams) => {
        if (!isPromotionsEnabled) {
            setPromotions([])
            return
        }

        try {
            setIsListLoading(true)
            const response = await listPromotions(params)
            setPromotions(response.data)
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        } finally {
            setIsListLoading(false)
        }
    }

    useEffect(() => {
        fetchPromotions(queryParams)
    }, [queryParams, isPromotionsEnabled])

    const handleApplyFilters = () => {
        setAppliedFilters(draftFilters)
    }

    const handleResetFilters = () => {
        setDraftFilters(defaultFilters)
        setAppliedFilters(defaultFilters)
    }

    const openCreateModal = () => {
        setCurrentPromotion(null)
        setFormState((prev) => {
            if (isBlobUrl(prev.imagePreview)) {
                URL.revokeObjectURL(prev.imagePreview)
            }
            return emptyFormState
        })
        setIsModalOpen(true)
    }

    const openEditModal = (promotion: IPromotion) => {
        setCurrentPromotion(promotion)
        setFormState((prev) => {
            if (isBlobUrl(prev.imagePreview)) {
                URL.revokeObjectURL(prev.imagePreview)
            }

            return {
                title: promotion.title,
                description: promotion.description,
                beginAt: toInputDateTime(promotion.begin_at),
                endAt: toInputDateTime(promotion.end_at),
                imageFile: null,
                imagePreview: promotion.image_url,
            }
        })
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setCurrentPromotion(null)
        setFormState((prev) => {
            if (isBlobUrl(prev.imagePreview)) {
                URL.revokeObjectURL(prev.imagePreview)
            }
            return emptyFormState
        })
    }

    const handleImageChange = (file: File | null) => {
        if (!file) {
            setFormState((prev) => {
                if (isBlobUrl(prev.imagePreview)) {
                    URL.revokeObjectURL(prev.imagePreview)
                }

                return {
                    ...prev,
                    imageFile: null,
                    imagePreview: currentPromotion?.image_url ?? "",
                }
            })
            return
        }

        const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`
        const hasValidExtension = acceptedExtensions.includes(extension)
        const hasValidMimeType = acceptedMimeTypes.includes(file.type)

        if (!hasValidExtension && !hasValidMimeType) {
            notify.error("Файл должен быть в формате PNG, JPG, JPEG или WEBP")
            return
        }

        if (file.size > maxImageSize) {
            notify.error("Размер файла не должен превышать 8 МБ")
            return
        }

        const imagePreview = URL.createObjectURL(file)
        setFormState((prev) => {
            if (isBlobUrl(prev.imagePreview)) {
                URL.revokeObjectURL(prev.imagePreview)
            }

            return {
                ...prev,
                imageFile: file,
                imagePreview,
            }
        })
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const title = normalizeString(formState.title)
        const description = normalizeString(formState.description)
        const beginAtIso = toIsoDateTime(formState.beginAt)
        const endAtIso = toIsoDateTime(formState.endAt)

        if (!title) {
            notify.error("Введите название акции")
            return
        }

        if (!description) {
            notify.error("Введите описание акции")
            return
        }

        if (!isEditing && !formState.imageFile) {
            notify.error("Добавьте изображение акции")
            return
        }

        try {
            setIsSubmitting(true)

            if (currentPromotion) {
                const payload: IUpdatePromotionPayload = {}

                if (title !== currentPromotion.title) {
                    payload.title = title
                }
                if (description !== currentPromotion.description) {
                    payload.description = description
                }
                if (beginAtIso && beginAtIso !== currentPromotion.begin_at) {
                    payload.begin_at = beginAtIso
                }
                if (endAtIso && endAtIso !== currentPromotion.end_at) {
                    payload.end_at = endAtIso
                }
                if (formState.imageFile) {
                    payload.image = formState.imageFile
                }

                if (!Object.keys(payload).length) {
                    notify.error("Нет изменений для сохранения")
                    return
                }

                await updatePromotion(currentPromotion.id, payload)
                notify.success("Акция успешно обновлена")
            } else {
                await createPromotion({
                    title,
                    description,
                    begin_at: beginAtIso,
                    end_at: endAtIso,
                    image: formState.imageFile as File,
                })
                notify.success("Акция успешно создана")
            }

            closeModal()
            fetchPromotions(queryParams)
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        } finally {
            setIsSubmitting(false)
        }
    }

    const openDeleteModal = (promotion: IPromotion) => {
        setPromotionToDelete(promotion)
        setIsDeleteModalOpen(true)
    }

    const closeDeleteModal = () => {
        if (isDeleting) {
            return
        }

        setIsDeleteModalOpen(false)
        setPromotionToDelete(null)
    }

    const handleConfirmDelete = async () => {
        if (!promotionToDelete) {
            return
        }

        try {
            setIsDeleting(true)
            await deletePromotion(promotionToDelete.id)
            notify.success("Акция удалена")
            fetchPromotions(queryParams)
            closeDeleteModal()
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <div className="_page_container">
            <PageHeader title="Акции" />
            <div className="flexbox">
                <div className="grid grid-cols-12 gap-4">
                    <DateTimeFilterField
                        label="Дата начала"
                        value={draftFilters.begin_at}
                        onChange={(value) => setDraftFilters((prev) => ({ ...prev, begin_at: value }))}
                    />
                    <DateTimeFilterField
                        label="Дата окончания"
                        value={draftFilters.end_at}
                        onChange={(value) => setDraftFilters((prev) => ({ ...prev, end_at: value }))}
                    />
                    <div className="col-span-6 sm:col-span-4 xl:col-span-2 flex items-end">
                        <button onClick={handleResetFilters} className="h-[52px] w-full text-center bg-white rounded-full">
                            Сбросить
                        </button>
                    </div>
                    <div className="col-span-6 sm:col-span-4 xl:col-span-2 flex items-end">
                        <button
                            onClick={handleApplyFilters}
                            className="h-[52px] w-full text-center bg-color-dark text-white rounded-full"
                        >
                            Применить
                        </button>
                    </div>
                    <div className="col-span-12 sm:col-span-4 xl:col-span-2 flex items-end justify-end">
                        <button
                            onClick={openCreateModal}
                            className="h-[52px] w-full text-center bg-color-dark text-white rounded-full"
                        >
                            Создать акцию
                        </button>
                    </div>
                </div>

                {!isPromotionsEnabled && !communityLoading && (
                    <div className="info_panel">
                        Сервис акций отключен для текущего сообщества.
                    </div>
                )}

                {canShowListLoader && (
                    <div className="info_panel">
                        <Loader />
                    </div>
                )}

                {!canShowListLoader && isPromotionsEnabled && !promotions.length && (
                    <NoTableData label="Нет данных об акциях" />
                )}

                {!canShowListLoader && isPromotionsEnabled && Boolean(promotions.length) && (
                    <div className="grid grid-cols-12 gap-4">
                        {promotions.map((promotion) => (
                            <div key={promotion.id} className="col-span-12 md:col-span-6 xl:col-span-4">
                                <div className="p-6 rounded-[16px] flex flex-col bg-white h-full">
                                    <div className="mb-6 rounded-[12px] overflow-hidden bg-white w-full h-[220px]">
                                        <img
                                            src={promotion.image_url}
                                            className="w-full h-full object-contain"
                                            alt={promotion.title}
                                        />
                                    </div>
                                    <div className="mb-3 text-color-dark text-2xl font-semibold">
                                        {promotion.title}
                                    </div>
                                    <div className="text-base font-normal mb-4 text-[#535862] whitespace-pre-wrap">
                                        {promotion.description}
                                    </div>
                                    <div className="text-sm text-[#777777] mb-1">
                                        Создана: {toLocaleDateTimeString(promotion.created_at)}
                                    </div>
                                    <div className="text-sm text-[#777777] mb-1">
                                        Начало: {promotion.begin_at ? toLocaleDateTimeString(promotion.begin_at) : "Не задано"}
                                    </div>
                                    <div className="text-sm text-[#777777] mb-6">
                                        Окончание: {promotion.end_at ? toLocaleDateTimeString(promotion.end_at) : "Не задано"}
                                    </div>
                                    <div className="mt-auto flex justify-end gap-2">
                                        <ActionButton
                                            onClick={() => openEditModal(promotion)}
                                            icon={PencilSvg}
                                            title="Редактировать"
                                        />
                                        <ActionButton
                                            onClick={() => openDeleteModal(promotion)}
                                            icon={TrashSvg}
                                            title="Удалить"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal
                isOpen={isModalOpen}
                title={isEditing ? "Редактировать акцию" : "Создать акцию"}
                onRequestClose={closeModal}
            >
                <form onSubmit={handleSubmit} className="flex flex-col gap-4 min-w-[560px]">
                    <div className="flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6]">
                        <label className="font-semibold uppercase text-base text-[#828282]">Название</label>
                        <input
                            value={formState.title}
                            onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder="Введите название"
                            className="w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                        />
                    </div>

                    <div className="flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6]">
                        <label className="font-semibold uppercase text-base text-[#828282]">Описание</label>
                        <textarea
                            rows={5}
                            value={formState.description}
                            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                            placeholder="Введите описание"
                            className="w-full outline-0 resize-none text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                        />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-6">
                            <label className="font-semibold uppercase text-base text-[#828282]">Дата начала</label>
                            <DatePicker
                                placeholderText="Выберите дату начала"
                                wrapperClassName="react-datepicker-form"
                                dateFormat={"dd.MM.yyyy HH:mm"}
                                showIcon
                                onChange={(date: Date | null) =>
                                    setFormState((prev) => ({ ...prev, beginAt: fromPickerDate(date) }))
                                }
                                selected={toPickerDate(formState.beginAt)}
                                enableTabLoop={false}
                                popperProps={{
                                    placement: "bottom",
                                }}
                                showTimeSelect
                                popperContainer={CalendarContainer}
                                popperClassName="z-[9999] react-datepicker-form-popper"
                                icon={<CalendarIcon width={24} height={24} />}
                            />
                        </div>
                        <div className="col-span-6">
                            <label className="font-semibold uppercase text-base text-[#828282]">Дата окончания</label>
                            <DatePicker
                                placeholderText="Выберите дату окончания"
                                wrapperClassName="react-datepicker-form"
                                dateFormat={"dd.MM.yyyy HH:mm"}
                                showIcon
                                onChange={(date: Date | null) =>
                                    setFormState((prev) => ({ ...prev, endAt: fromPickerDate(date) }))
                                }
                                selected={toPickerDate(formState.endAt)}
                                enableTabLoop={false}
                                popperProps={{
                                    placement: "bottom",
                                }}
                                showTimeSelect
                                popperContainer={CalendarContainer}
                                popperClassName="z-[9999] react-datepicker-form-popper"
                                icon={<CalendarIcon width={24} height={24} />}
                                minDate={toPickerDate(formState.beginAt) ?? undefined}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="font-semibold uppercase text-base text-[#828282]">Изображение</label>
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp"
                            onChange={(e) => handleImageChange(e.target.files?.[0] ?? null)}
                        />
                        {Boolean(formState.imagePreview) && (
                            <div className="w-full h-[200px] rounded-xl overflow-hidden bg-[#F7F8FA]">
                                <img src={formState.imagePreview} alt="" className="w-full h-full object-contain" />
                            </div>
                        )}
                        <div className="text-sm text-[#777777]">
                            Поддерживаются PNG, JPG, JPEG, WEBP. Максимум 8 МБ.
                        </div>
                    </div>

                    <div className="w-full flex gap-3">
                        <button onClick={closeModal} type="button" className="form_cancel_button">
                            Отменить
                        </button>
                        <button disabled={isSubmitting} className="form_handle_button" type="submit">
                            {isEditing ? "Сохранить" : "Создать"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={isDeleteModalOpen} title="Удаление акции" onRequestClose={closeDeleteModal}>
                <div className="flex flex-col gap-3">
                    <div>
                        Вы уверены, что хотите удалить акцию
                        {promotionToDelete ? ` "${promotionToDelete.title}"` : ""}?
                    </div>
                    <div className="w-full flex gap-3">
                        <button
                            onClick={closeDeleteModal}
                            disabled={isDeleting}
                            type="button"
                            className="form_cancel_button"
                        >
                            Отменить
                        </button>
                        <button disabled={isDeleting} onClick={handleConfirmDelete} className="form_handle_button" type="button">
                            Удалить
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
